import { RecipientModel } from '../../models/low-level/recipient.model';
import { RecipientEntryState } from './recipients-collection.state';

/**
 * Converters at the boundary between `RecipientModel` and the model-free
 * `RecipientEntryState` / `SavedRecipientsState` shapes. These are the only files in the
 * recipients slice allowed to import a legacy model class — actions, reducers, selectors
 * and effects stay model-free (see `architecture-rules.md`).
 */

/**
 * Reads a recipient model's fields into the slice's flat, serializable shape.
 *
 * @param model the recipient model to convert
 * @returns the plain state representation
 */
export function recipientModelToState(model: RecipientModel): RecipientEntryState {
    return {
        faxEmail: model.faxEmail.value,
        commCategory: model.commCategory.value,
        isSelected: !!model.isSelected.model.value,
        isEditable: model.isEditable,
        isDeletable: model.isDeletable,
        isHidden: model.isHidden,
        isFromEmailSet: model.isFromEmailSet,
        isSubmitted: model.isSubmitted,
    };
}

/**
 * Builds a recipient model from the slice's flat shape.
 *
 * The round-trip `model → state → model` is identity except for an invalid email: the
 * `RecipientModel` constructor recomputes `isSelected = isSelectable && isSelected` via
 * `DataMaintenanceUtilities.isEmailInvalid`. `RecipientModelFromJson` behaves identically
 * today, so this is not a regression introduced by the slice.
 *
 * @param state the flat state to convert
 * @returns the recipient model
 */
export function recipientStateToModel(state: RecipientEntryState): RecipientModel {
    return new RecipientModel(
        state.faxEmail,
        state.commCategory,
        state.isSelected,
        state.isEditable,
        state.isDeletable,
        state.isHidden,
        state.isFromEmailSet,
        state.isSubmitted,
    );
}

/**
 * Converts a collection of recipient models to their flat state shape.
 *
 * @param recipients the recipient models to convert
 * @returns the plain state representations
 */
export function recipientsToState(recipients: RecipientModel[]): RecipientEntryState[] {
    return recipients.map(recipientModelToState);
}

/**
 * Converts a collection of flat recipient states back to recipient models.
 *
 * @param entries the flat states to convert
 * @returns the recipient models
 */
export function recipientsFromState(entries: RecipientEntryState[]): RecipientModel[] {
    return entries.map(recipientStateToModel);
}
